/**
 * mermaid.render() with each caller's own config, and without the stray error graphic.
 *
 * Two things go wrong with calling mermaid directly:
 *
 * - Its config is global. `mermaid.initialize()` replaces it, so whichever component initialised last decided the
 *   theme and diagram settings of every other component's diagrams. Each render here applies the caller's config
 *   immediately before it renders, one render at a time, so a diagram is drawn with its own settings.
 * - When a render fails (bad syntax, or a diagram chunk that no longer loads after a deploy) mermaid draws its
 *   "Syntax error in text / mermaid version" bomb into a temporary <div id="d{id}"> appended to document.body and
 *   throws before removing it. That div is outside every layout, so the bomb sat at the bottom-left of the page
 *   over the footer while the component showed its own error box as well. Every render here sets
 *   `suppressErrorRendering` (mermaid then throws without drawing), and also removes any temp node a render left
 *   behind, so the component's own error box is the only place a failure shows.
 */

import mermaid, { type MermaidConfig, type RenderResult } from "mermaid";

/** The temp nodes mermaid.render(id, …) appends to document.body: the
 * enclosing div, and the iframe in sandbox security mode. */
export function removeMermaidTempNodes(id: string) {
  for (const nodeId of [`d${id}`, `i${id}`])
    document.getElementById(nodeId)?.remove();
}

// One render at a time: a config applied for one render must not be replaced by another component's before it runs.
let queue: Promise<unknown> = Promise.resolve();

export function renderMermaid(
  id: string,
  definition: string,
  config: MermaidConfig,
): Promise<RenderResult> {
  const run = async () => {
    mermaid.initialize({
      ...config,
      startOnLoad: false,
      suppressErrorRendering: true,
    });
    try {
      return await mermaid.render(id, definition);
    } finally {
      removeMermaidTempNodes(id);
    }
  };
  const result = queue.then(run, run);
  queue = result.catch(() => undefined);
  return result;
}
